// What we can and cannot currently say, above every figure on the page.
//
// This is the first thing on the screen on purpose. The cash reconciliation has
// four independent inputs and on the day it was built three were absent. A person
// reading a variance without knowing which leg is missing will act on a number
// that is mostly a broken form, and the action is a conversation with a driver
// about money he may well have banked.
//
// Everything here is a statement about FLEETLY'S RECORDS. Nothing here is about a
// driver, and the wording keeps saying so because the reader will be under
// pressure and will read fast.
import { AlertCircle, CheckCircle2, Info } from 'lucide-react'

export type Coverage = {
  uber_cash_from: string | null
  uber_cash_to: string | null
  uber_cash_rows: number | string | null
  uber_trip_rows: number | string | null
  part_a_last: string | null
  part_b_last: string | null
  part_a_count: number | string | null
  part_b_count: number | string | null
  deposit_accounts: number | string | null
  statement_lines: number | string | null
  bank_covered_from: string | null
  bank_covered_to: string | null
  fuel_rows: number | string | null
  fuel_from: string | null
  fuel_to: string | null
  stations_known: number | string | null
  stations_geocoded: number | string | null
  deposit_slips: number | string | null
  deposit_slips_read: number | string | null
  fuel_receipts: number | string | null
  fuel_receipts_read: number
  fuel_receipts_cash?: number | string | null
  declarations_from: string | null
}

function day(v: string | null): string {
  if (!v) return 'never'
  return new Date(v).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
}

function daysSince(v: string | null): number | null {
  if (!v) return null
  return Math.floor((Date.now() - new Date(v).getTime()) / 86_400_000)
}

type Line = { tone: 'ok' | 'warn' | 'gap'; text: string }

export function coverageLines(c: Coverage): Line[] {
  const out: Line[] = []

  out.push({
    tone: Number(c.uber_cash_rows) > 0 ? 'ok' : 'gap',
    text:
      Number(c.uber_cash_rows) > 0
        ? `Uber cash is loaded from ${day(c.uber_cash_from)} to ${day(c.uber_cash_to)}.` +
          (Number(c.uber_trip_rows) > 0
            ? ' Per-trip cash is loaded too, so the figures use the stronger source.'
            : ' Per-trip cash has never been loaded, so the figures use Uber’s per-shift report.')
        : 'No Uber cash has been loaded at all. Nothing on this page can be reconciled until it is.',
  })

  const bLag = daysSince(c.part_b_last)
  if (bLag !== null && bLag > 2) {
    out.push({
      tone: 'warn',
      text:
        `Part B submissions stopped landing on ${day(c.part_b_last)}, ${bLag} days ago. ` +
        'Cash spent on petrol and cash exchanged with a team mate are therefore unknown for every shift since, ' +
        'so those shifts show an indicative figure and never a shortfall. Lateness fines and rebates are not being recorded either.',
    })
  } else if (bLag === null) {
    out.push({ tone: 'gap', text: 'No Part B submission has ever landed.' })
  }

  const aLag = daysSince(c.part_a_last)
  if (aLag !== null && aLag > 2) {
    out.push({
      tone: 'warn',
      text: `Part A submissions stopped landing on ${day(c.part_a_last)}, ${aLag} days ago.`,
    })
  }

  out.push({
    tone: Number(c.statement_lines) > 0 ? 'ok' : 'gap',
    text:
      Number(c.deposit_accounts) === 0
        ? 'No Fleetly deposit account is registered, so no deposit can be checked against a statement. Register the account under Sources first.'
        : Number(c.statement_lines) > 0
          ? `Bank statement loaded for ${day(c.bank_covered_from)} to ${day(c.bank_covered_to)}. Deposits outside those dates read "no statement loaded", which is not a finding.`
          : 'No bank statement has been loaded, so no deposit can be confirmed as having reached Fleetly. Every deposit reads "no statement loaded".',
  })

  // THIS LINE USED TO SAY "Fuel transactions loaded", WHICH READ AS A SUPPLIER
  // REPORT AND IS NOT ONE. Every row in fuel_card_transactions carries
  // source = 'shift_form_sheet' and fleet_card_number = 'NONE-SHIFT-RECEIPT': it
  // is the driver's own declared spend off his daily submission, going back to
  // the first week Fleetly traded. fuel_transaction_uploads is empty and no row
  // names a station, so no Kilomita or FuelEasy report has ever been loaded.
  //
  // The distinction is the whole petrol question. A declaration cannot check
  // itself, so until a supplier report arrives nothing can say which of that
  // petrol was paid in cash. The banner says that in those words, because the
  // old wording sent a reader looking for a driver's mistake instead of a
  // missing document.
  out.push({
    tone: Number(c.stations_known) > 0 ? 'ok' : 'gap',
    text:
      Number(c.fuel_rows) > 0
        ? `Petrol is declared on the daily submission from ${day(c.fuel_from)} to ${day(c.fuel_to)}, and that is the only petrol on file. ` +
          (Number(c.stations_known) > 0
            ? `${c.stations_geocoded} of ${c.stations_known} stations are on the map; transactions at the rest cannot have a distance checked.`
            : 'No Kilomita or FuelEasy report has ever been loaded, so nothing can say which of it was paid in cash. Petrol reads "not validated" for that reason, and it is our gap, not a driver\u2019s.')
        : 'No petrol is on file at all, so nothing claimed for petrol can be checked against anything.',
  })

  // THIS LINE SAID "none has been read" FOR A DAY AFTER SEVERAL HUNDRED HAD BEEN,
  // because it counted shift_photos.ocr_extracted, which nothing has ever
  // written. The reader writes to shift_document_reading and the handler now
  // counts from there. A banner that reports a gap Fleetly has already closed
  // sends somebody to solve a solved problem.
  const slips = Number(c.deposit_slips)
  const receipts = Number(c.fuel_receipts)
  const slipsRead = Number(c.deposit_slips_read)
  const receiptsRead = Number(c.fuel_receipts_read)
  const cashReceipts = Number(c.fuel_receipts_cash ?? 0)

  if (slipsRead === 0 && receiptsRead === 0) {
    out.push({
      tone: 'gap',
      text: `${slips} deposit slips and ${receipts} fuel receipts are on file and none has been read. A photograph proves only that one was taken.`,
    })
  } else if (receiptsRead < receipts) {
    out.push({
      tone: 'gap',
      text:
        `${receiptsRead} of ${receipts} fuel receipts have been read and ${cashReceipts} of those say the driver paid cash. ` +
        `The rest are still being read, so a petrol figure that looks low today may simply not have got there yet.`,
    })
  } else {
    out.push({
      tone: 'ok',
      text:
        `Every fuel receipt on file has been read, ${cashReceipts} of ${receipts} saying cash, and ${slipsRead} of ${slips} deposit slips. ` +
        `Only a slip that says cash and ties to what was declared reduces what a driver owes.`,
    })
  }

  return out
}

export function CoverageBanner({ coverage }: { coverage: Coverage | null }) {
  if (!coverage) return null
  const lines = coverageLines(coverage)
  const anyGap = lines.some((l) => l.tone !== 'ok')

  return (
    <div className="border-b border-border bg-muted/40 px-4 py-3">
      <div className="flex items-center gap-2">
        <Info className="h-4 w-4 shrink-0 text-muted-foreground" />
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          What we can say today
        </h2>
      </div>
      <ul className="mt-2 space-y-1.5">
        {lines.map((l, i) => (
          <li key={i} className="flex items-start gap-2 text-xs">
            {l.tone === 'ok' ? (
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
            ) : (
              <AlertCircle
                className={
                  l.tone === 'warn'
                    ? 'mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400'
                    : 'mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground'
                }
              />
            )}
            <span className={l.tone === 'ok' ? 'text-muted-foreground' : 'text-foreground'}>{l.text}</span>
          </li>
        ))}
      </ul>
      {anyGap && (
        <p className="mt-2 text-xs text-muted-foreground">
          Every gap above is in Fleetly&rsquo;s records. None of it is a finding about a driver, and a
          row on this page that says data is missing must never be taken to one.
        </p>
      )}
    </div>
  )
}
